import React, { useState } from 'react';
import { Calculator, DollarSign, TrendingUp, Building, CreditCard, PiggyBank, Car, Globe, Landmark } from 'lucide-react';
import JurosCompostos from '../components/calculators/JurosCompostos';
import Financiamento from '../components/calculators/Financiamento';
import CartaoCredito from '../components/calculators/CartaoCredito';
import Investimento from '../components/calculators/Investimento';
import Economia from '../components/calculators/Economia';
import CarroAssinatura from '../components/calculators/CarroAssinatura';
import ComparadorRendaFixa from '../components/calculators/ComparadorRendaFixa';
import ContaGlobal from '../components/calculators/ContaGlobal';
import { useSEO } from '../hooks/useSEO';

const Calculators: React.FC = () => {
  // SEO Implementation
  useSEO({
    title: 'Calculadoras Financeiras Gratuitas 2026: Juros, LCI vs CDB e Carro por Assinatura | O Teu Dinheiro',
    description: 'Simule juros compostos, equivalência LCI/LCA vs CDB, carro por assinatura vs compra, câmbio Nomad/Wise, financiamentos e dívidas de cartão gratuitamente.',
    keywords: 'calculadora financeira, juros compostos, calculadora carro por assinatura, simulador LCI LCA vs CDB, câmbio conta global, simulação financiamento',
    url: 'https://www.oteudinheiro.com.br/calculadoras',
    image: 'https://www.oteudinheiro.com.br/assets/calculators-preview.png'
  });

  const [activeCalculator, setActiveCalculator] = useState('juros-compostos');

  const calculators = [
    {
      id: 'juros-compostos',
      name: 'Juros Compostos',
      icon: <TrendingUp className="w-6 h-6" />,
      description: 'Calcule o crescimento do seu dinheiro ao longo do tempo'
    },
    {
      id: 'renda-fixa',
      name: 'LCI/LCA vs. CDB',
      icon: <Landmark className="w-6 h-6" />,
      description: 'Compare taxas equivalentes com e sem Imposto de Renda'
    },
    {
      id: 'carro-assinatura',
      name: 'Carro por Assinatura vs. Compra',
      icon: <Car className="w-6 h-6" />,
      description: 'Compare IPVA, seguro, depreciação e CDI contra a assinatura'
    },
    {
      id: 'conta-global',
      name: 'Câmbio: Wise vs. Nomad vs. Cartão',
      icon: <Globe className="w-6 h-6" />,
      description: 'Veja quanto economizar em dólar ou euro na próxima viagem'
    },
    {
      id: 'financiamento',
      name: 'Financiamento Imobiliário',
      icon: <Building className="w-6 h-6" />,
      description: 'Simule parcelas e juros de financiamento'
    },
    {
      id: 'cartao-credito',
      name: 'Cartão de Crédito',
      icon: <CreditCard className="w-6 h-6" />,
      description: 'Calcule juros e tempo para pagar dívidas'
    },
    {
      id: 'investimento',
      name: 'Investimento',
      icon: <DollarSign className="w-6 h-6" />,
      description: 'Simule retornos de diferentes investimentos'
    },
    {
      id: 'economia',
      name: 'Meta de Economia',
      icon: <PiggyBank className="w-6 h-6" />,
      description: 'Calcule quanto economizar para atingir seus objetivos'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white py-20">
        <div className="container-custom text-center">
          <Calculator className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Calculadoras Financeiras
          </h1>
          <p className="text-xl md:text-2xl text-indigo-100 max-w-3xl mx-auto">
            Ferramentas para planejar suas finanças e tomar decisões mais inteligentes
          </p>
        </div>
      </section>

      {/* Calculator Selection */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Escolha a calculadora que você precisa
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {calculators.map((calc) => (
              <button
                key={calc.id}
                onClick={() => setActiveCalculator(calc.id)}
                className={`p-6 rounded-xl border-2 transition-all duration-200 text-left ${activeCalculator === calc.id
                  ? 'border-indigo-500 bg-indigo-50 shadow-lg'
                  : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-md'
                  }`}
              >
                <div className="flex items-center space-x-3 mb-3">
                  <div className={`p-2 rounded-lg ${activeCalculator === calc.id ? 'bg-indigo-100' : 'bg-gray-100'
                    }`}>
                    {calc.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{calc.name}</h3>
                </div>
                <p className="text-gray-600 text-sm">{calc.description}</p>
              </button>
            ))}
          </div>

          {/* Calculator Content */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            {activeCalculator === 'juros-compostos' && <JurosCompostos />}
            {activeCalculator === 'renda-fixa' && <ComparadorRendaFixa />}
            {activeCalculator === 'carro-assinatura' && <CarroAssinatura />}
            {activeCalculator === 'conta-global' && <ContaGlobal />}
            {activeCalculator === 'financiamento' && <Financiamento />}
            {activeCalculator === 'cartao-credito' && <CartaoCredito />}
            {activeCalculator === 'investimento' && <Investimento />}
            {activeCalculator === 'economia' && <Economia />}
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Dicas para usar as calculadoras
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Seja realista
              </h3>
              <p className="text-secondary-700">
                Use valores realistas baseados na sua situação atual.
                É melhor ser conservador nas projeções.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Compare cenários
              </h3>
              <p className="text-secondary-700">
                Teste diferentes valores e prazos para encontrar
                a melhor estratégia para seus objetivos.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Considere a inflação
              </h3>
              <p className="text-secondary-700">
                Lembre-se que o poder de compra do dinheiro diminui
                com o tempo devido à inflação.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Revise periodicamente
              </h3>
              <p className="text-secondary-700">
                Recalcule seus planos regularmente conforme sua
                situação financeira muda.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Calculators;
